import { css, SerializedStyles } from '@emotion/react'
import { ReactElement } from 'react'
import { useLocation } from 'react-router-dom'

import IconButton from '../../buttons/IconButton'
import useMixpanelEvent from '../../../../lib/hooks/useMixpanelEvent'
import useTheme from '../../../../lib/hooks/useTheme'
import { getFontColor, getFocusColor } from '../../../../lib/colorUtils'
import { userIsAdmin } from '../../../../lib/authenticationUtils'
import { Auth0User } from '../../../../types'
import { Target } from '../../buttons/Button'

type NavBarButtonProps = {
  /** Accessibility label to be applied to the button */
  ariaLabel: string
  /** Optional aria-controls attribute to indicate which element this button controls */
  ariaControls?: string
  /** Optional aria-pressed attribute for toggle buttons */
  ariaPressed?: boolean
  /** Optional CSS overwrite for the button */
  buttonCss?: SerializedStyles
  /** Optional expanded state for aria-expanded attribute */
  expanded?: boolean
  /** Icon element for the button */
  icon: ReactElement
  /** Whether this is a redirect outside of the student app.
   * Also needs the route prop to redirect properly.
   */
  isExternalLink?: boolean
  /** Whether the NavBar parent is expanded */
  isNavExpanded: boolean
  /** Whether the NavBar parent is persisted */
  isNavPersisted: boolean
  /** Label to apply when the NavBar is expanded */
  label: string
  /** Optional CSS overwrite for the label */
  labelCss?: SerializedStyles
  /** Optional click handler */
  onClick?: () => void
  /** Function to shrink the NavBar */
  onShrink?: () => void
  /** The route that is represented by this button
   * Note: Some nav buttons might not represent routes */
  route?: string
  /** Paired with isExternalLink. Specifies where to open the linked document. */
  target?: Target
  /** If we should add a mixpanel event onClick of the button */
  trackNavigation?: boolean
  /** User object from useAuth0 */
  user?: Auth0User | undefined
}

/** IMPORTANT:  This code is duplicated in scheduler.
 * Verify any changes here are also made in scheduler.
 */
const NavBarButton = ({
  ariaLabel,
  ariaControls,
  ariaPressed,
  buttonCss,
  expanded,
  onShrink,
  icon,
  isNavExpanded,
  isNavPersisted,
  isExternalLink = false,
  label,
  labelCss,
  onClick,
  route,
  target,
  trackNavigation = false,
  user
}: NavBarButtonProps) => {
  const { navbarColor } = useTheme()
  const fontColor = getFontColor(navbarColor!)
  const { addMixpanelEvent } = useMixpanelEvent()

  const handleClick = () => {
    if (onShrink && !isNavPersisted) {
      onShrink()
    }

    if (trackNavigation) {
      addMixpanelEvent(`Navigate to ${label}`)
    }

    if (onClick) {
      onClick()
    }
  }

  const labelStyle =
    labelCss ||
    css`
      color: ${fontColor};
      display: inline-block;
      padding-left: 15px;
      line-height: 26px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `
  const { pathname } = useLocation()
  const isAdmin = userIsAdmin(user)
  const isActive =
    (route && pathname === route) ||
    (!isAdmin && route === '/plans' && pathname === '/') ||
    (isAdmin && route === '/dashboard' && pathname === '/')
  const buttonStyle =
    buttonCss ||
    css`
      ${isNavExpanded && 'justify-content: start;'}
      border-radius: 4px;
      align-content: center;
      color: ${fontColor};
      display: flex;
      font-size: 1rem;
      grid-column: 1;
      height: 36px;
      justify-content: ${isNavExpanded ? 'start' : 'center'};
      opacity: ${isActive ? '100%' : '80%'};
      text-transform: none;
      transition: all 0.3s ease;
      width: ${isNavExpanded ? '168px' : '16px'};

      :hover,
      :focus {
        background: ${getFocusColor(navbarColor!, undefined, true)};
        color: ${fontColor};
        cursor: pointer;
      }

      svg {
        vertical-align: top;
      }

      a {
        padding: 0px 16px;
        text-align: center;
      }
    `

  const nonRouteButtonContainerCss = css`
    color: ${fontColor};
    display: flex;
    align-content: center;
    justify-content: ${isNavExpanded ? 'start' : 'center'};

    button {
      height: 48px;
      width: ${isNavExpanded ? '230px' : '52px'};
      padding: 12px 16px;
    }
  `

  // WCAG 4.1.2: Only pass expanded when explicitly set to avoid
  // aria-expanded being rendered as false/undefined on navigation buttons
  const expandedProps = expanded !== undefined ? { expanded } : {}

  return route ? (
    <IconButton
      ariaLabel={ariaLabel}
      ariaControls={ariaControls}
      ariaPressed={ariaPressed}
      color={navbarColor!}
      css={buttonStyle}
      displayTooltip={false}
      exact={route === '/'}
      {...expandedProps}
      href={route}
      isActive={isActive}
      isExternalLink={isExternalLink}
      isNav
      onClick={handleClick}
      target={target}
    >
      {icon}
      {isNavExpanded && <div css={labelStyle}>{label}</div>}
    </IconButton>
  ) : (
    <div css={nonRouteButtonContainerCss}>
      <IconButton
        ariaLabel={ariaLabel}
        ariaControls={ariaControls}
        ariaPressed={ariaPressed}
        color={navbarColor!}
        css={buttonStyle}
        displayTooltip={false}
        {...expandedProps}
        onClick={handleClick}
      >
        {icon}
        {isNavExpanded && <div css={labelStyle}>{label}</div>}
      </IconButton>
    </div>
  )
}

export default NavBarButton
